import React, { useEffect, useState } from "react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import { useApp } from "../../hooks/useApp.js";
import { getServiceFeePaymentHistory } from "../../services/paymentDetailsService.js";
import { money } from "../../utils/format.js";
import PaymentReceiptModal from "./PaymentReceiptModal.jsx";

const PAGE_SIZE = 10;

const FEE_STATUS_STYLES = {
  SUCCESS: { label: "Paid", bg: "var(--accent-soft)", color: "var(--accent)" },
  PENDING: { label: "Pending", bg: "var(--amber-soft)", color: "var(--amber)" },
  FAILED: { label: "Failed", bg: "var(--danger-soft)", color: "var(--danger)" },
  REFUNDED: { label: "Refunded", bg: "var(--panel-2)", color: "var(--muted)" },
};

function FeeBadge({ status }) {
  const s = FEE_STATUS_STYLES[status] || { label: status || "—", bg: "var(--panel-2)", color: "var(--muted)" };
  return (
    <span
      className="inline-flex items-center gap-1 px-[9px] py-1 rounded-[20px] text-[12px] font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const PaymentHistory = () => {
  const { showToast } = useApp();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [receipt, setReceipt] = useState(null); // { payment, autoSend }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const rows = await getServiceFeePaymentHistory();
        if (!cancelled) setPayments(rows || []);
      } catch (err) {
        if (!cancelled) showToast(err.message || "Couldn't load payment history.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const successPayments = payments.filter((p) => p.status === "SUCCESS");
  const totalPaid = successPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const lastPayment = successPayments.length > 0 ? successPayments[0] : null;
  const hasPayments = successPayments.length > 0;

  const stats = [
    {
      label: "Total paid",
      value: hasPayments ? money(totalPaid) : "—",
      sub: hasPayments ? `${successPayments.length} payment${successPayments.length !== 1 ? "s" : ""}` : "No payments yet",
    },
    {
      label: "Last payment",
      value: lastPayment ? money(lastPayment.amount) : "—",
      sub: lastPayment ? fmtDate(lastPayment.date) : "No payments yet",
    },
    {
      label: "Payment method",
      value: lastPayment?.paymentMethod || "—",
      sub: lastPayment ? "Most recent" : "",
    },
  ];

  const totalPages = Math.ceil(payments.length / PAGE_SIZE);
  const pageRows = payments.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="mb-5">
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Finance
        </div>
        <h1 className="text-[28px] leading-[1.1] mt-1">Payments</h1>
        <p className="text-[var(--muted)] max-w-[62ch]">
          Service fee payment history and receipts for your Form 2290 filings.
        </p>
      </div>

      <div className="grid gap-[14px] mb-6 sm:grid-cols-3">
        {stats.map((s) => (
          <Panel key={s.label} padded>
            <div className="text-[11px] font-bold text-[var(--muted)] uppercase tracking-[0.08em] mb-2 pb-[6px] border-b border-[var(--line)]">
              {s.label}
            </div>
            <div className="text-[22px] font-extrabold text-[var(--ink)] tracking-[-0.02em]">{s.value}</div>
            {s.sub && <div className="text-[13px] text-[var(--muted)] mt-[3px]">{s.sub}</div>}
          </Panel>
        ))}
      </div>

      <Panel className="overflow-hidden">
        <div className="py-4 px-5 border-b border-[var(--line)] flex items-center justify-between">
          <div className="text-[13px] font-bold text-[var(--ink)]">Payment history</div>
          <span className="text-[13px] text-[var(--muted)]">Powered by Stripe</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[var(--muted)]">Loading…</div>
        ) : payments.length === 0 ? (
          <div className="p-10 text-center text-[var(--muted)]">
            Your payment history will appear here after you submit your first Form 2290 filing.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[760px]">
                <thead>
                  <tr className="border-b border-[var(--line)]">
                    {["Date", "Description", "Amount", "Status", "Confirmation #", "Receipt"].map((h) => (
                      <th
                        key={h}
                        className="text-[12px] font-semibold text-[var(--muted)] uppercase tracking-[0.06em] py-3 px-4 text-left bg-[var(--panel-2)]"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((p) => (
                    // Keyed by source + id, not id alone - ServiceFeePayment and
                    // SubscriptionReturnUsage ids come from different tables/sequences and
                    // could otherwise collide across the two merged sources (see
                    // ServiceFeeServiceImpl.getPaymentHistory).
                    <tr key={`${p.source}-${p.id}`} className="border-b border-[var(--line)] last:border-b-0 hover:bg-[var(--accent-soft)] transition-colors">
                      <td className="py-3.5 px-4 text-[13px] text-[var(--muted)] whitespace-nowrap">{fmtDate(p.date)}</td>
                      <td className="py-3.5 px-4 text-[14px] font-semibold text-[var(--ink)]">
                        {p.description || "Form 2290 e-filing service fee"}
                        {p.businessName && <div className="text-[12px] font-normal text-[var(--muted)]">{p.businessName}</div>}
                      </td>
                      <td className="py-3.5 px-4 text-[14px] font-bold text-[var(--ink)] whitespace-nowrap">{money(p.amount)}</td>
                      <td className="py-3.5 px-4">
                        <FeeBadge status={p.status} />
                      </td>
                      <td className="py-3.5 px-4 text-[12px] font-mono text-[var(--muted)] whitespace-nowrap">
                        {p.confirmationNumber || "—"}
                      </td>
                      <td className="py-3.5 px-4">
                        {/* A subscription-credit row now gets a real credit-redemption receipt
                            too (backend: ServiceFeeServiceImpl.toSubscriptionCreditReceiptResponse,
                            keyed off p.id + p.source=SUBSCRIPTION_CREDIT) - not a fresh Stripe
                            charge, but a real document referencing the package purchase, prorated
                            to this return. Same View Receipt/Resend buttons as a Stripe-paid row. */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="!text-xs !px-3 !py-1.5"
                            onClick={() => setReceipt({ payment: p, autoSend: false })}
                          >
                            View Receipt
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="!text-xs !px-3 !py-1.5"
                            onClick={() => setReceipt({ payment: p, autoSend: true })}
                          >
                            Resend
                          </Button>
                        </div>
                        {(p.source === "SUBSCRIPTION_CREDIT" || p.source === "SUBSCRIPTION_TOPUP_CREDIT") && (
                          <div className="text-[11px] text-[var(--muted)] mt-1">Paid by subscription credit</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {payments.length > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-[var(--line)]">
                <div className="text-[13px] text-[var(--muted)]">
                  Page {page + 1} of {totalPages}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!text-xs !px-3 !py-1.5"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    ← Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!text-xs !px-3 !py-1.5"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>

      {receipt && (
        <PaymentReceiptModal
          payment={receipt.payment}
          autoSend={receipt.autoSend}
          onClose={() => setReceipt(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default PaymentHistory;
